// ============================================================
//  Aggregation — alle abgeleiteten Kennzahlen, ratio-bewusst.
//  Rechnet immer über PERSONEN (R1), Klasse als Maximum (R2),
//  Hebel effektiv (R3), Warenkorb als Zielzustand-Union (R4).
// ============================================================

import type { Driver, Lever, Person, TraceState } from "./types";
import { DEFAULT_PRICE_PER_FUE, DEFAULT_RATIO, type RatioTable, weight } from "./rules";
import { assignedLandscapes } from "../data/landscapes";
import { isFueRelevantLandscape } from "../data/licensing";

/** Realisierbare FUE-Reduktion einer Person (Ist − Ziel). */
export function personR(p: Person, ratio: RatioTable = DEFAULT_RATIO): number {
  return weight(p.licenseClass, ratio) - weight(p.cleanClass, ratio);
}

/**
 * Trace-Status der Reduktion einer Person — über ALLE entfernbaren Treiber auf dem obersten Step:
 * gefahrlos („nie-genutzt") nur, wenn keiner davon genutzt wurde; „genutzt", sobald einer genutzt
 * ist; sonst „kein-trace". Vorher zählte der ERSTE entfernbare Treiber — bei mehreren Über-Grants
 * je Person (Seed-Daten) wurde so Reduktion als gefahrlos gezählt, die es nicht war.
 */
function removableTrace(p: Person): TraceState | null {
  const rem = p.drivers.filter((x: Driver) => x.removable && x.leverId && p.leverIds.includes(x.leverId));
  const pool = rem.length ? rem : p.drivers.filter((x: Driver) => x.removable);
  if (!pool.length) return null;
  if (pool.some((d) => d.trace === "genutzt")) return "genutzt";
  if (pool.every((d) => d.trace === "nie-genutzt")) return "nie-genutzt";
  return "kein-trace";
}

export interface Totals {
  ist: number;
  clean: number;
  leversTotal: number;
  green: number;
  amber: number;
  gray: number;
  afterSafe: number;
}

export function totals(persons: Person[], ratio: RatioTable = DEFAULT_RATIO): Totals {
  let ist = 0, clean = 0, green = 0, amber = 0, gray = 0;
  for (const p of persons) {
    ist += weight(p.licenseClass, ratio);
    clean += weight(p.cleanClass, ratio);
    if (p.leverIds.length > 0) {
      const r = personR(p, ratio);
      if (r > 0) {
        const t = removableTrace(p);
        if (t === "nie-genutzt") green += r;
        else if (t === "genutzt") amber += r;
        else gray += r;
      }
    }
  }
  const leversTotal = green + amber + gray;
  return { ist, clean, leversTotal, green, amber, gray, afterSafe: ist - green };
}

/** Standard-Vertragszahl. Die wirksame Zahl kommt aus der Vertragsgrundlage je Klasse (store/contract.ts). */
export const FUE_CONTRACTED = 19900;
import { contractedFue } from "../store/contract";

export interface Kpis extends Totals {
  entitlement: number;
  delta: number;
  deltaEuro: number;
  restLuecke: number;
  price: number;
}

export function kpis(
  persons: Person[],
  ratio: RatioTable = DEFAULT_RATIO,
  price: number = DEFAULT_PRICE_PER_FUE,
): Kpis {
  const t = totals(persons, ratio);
  const entitlement = contractedFue(ratio);
  const delta = t.ist - entitlement;
  return {
    ...t,
    entitlement,
    delta,
    deltaEuro: delta * price, // I8: Preis ist Parameter
    restLuecke: t.afterSafe - entitlement,
    price,
  };
}

/** R4: Warenkorb als Zielzustand. Personen im Schnitt zweier Hebel zählen EINMAL. */
export function basket(
  selected: string[],
  persons: Person[],
  ratio: RatioTable = DEFAULT_RATIO,
  price: number = DEFAULT_PRICE_PER_FUE,
) {
  const sel = new Set(selected);
  let reduction = 0;
  for (const p of persons) {
    if (p.leverIds.some((id) => sel.has(id))) reduction += personR(p, ratio);
  }
  const ist = totals(persons, ratio).ist;
  return { reduction, afterBasket: ist - reduction, euroSaved: reduction * price };
}

/** Nur die reduzierbare Menge eines einzelnen Hebels (für Δ-Vergleiche/I5). */
export function leverDelta(leverId: string, persons: Person[], ratio: RatioTable = DEFAULT_RATIO): number {
  let r = 0;
  for (const p of persons) if (p.leverIds.includes(leverId)) r += personR(p, ratio);
  return r;
}

export interface LeverStat {
  id: string;
  affected: number;
  effective: number;
  deltaFue: number;
}

export function leverStats(levers: Lever[], persons: Person[], ratio: RatioTable = DEFAULT_RATIO): LeverStat[] {
  return levers.map((lv) => {
    let affected = 0, effective = 0, deltaFue = 0;
    for (const p of persons) {
      const holds = p.drivers.some((d) => d.removable && d.leverId === lv.id);
      if (holds) affected++;
      if (p.leverIds.includes(lv.id)) {
        const r = personR(p, ratio);
        if (r > 0) {
          effective++;
          deltaFue += r;
        }
      }
    }
    return { id: lv.id, affected, effective, deltaFue };
  });
}

/** Personen je Landschaft (Σ > Gesamtzahl, I3) + Klassenverteilung. */
export function landscapeStats(persons: Person[]) {
  const per: Record<string, { count: number; classes: Record<string, number> }> = {};
  for (const l of assignedLandscapes) per[l.id] = { count: 0, classes: {} };
  for (const p of persons) {
    for (const lid of p.landscapeIds) {
      if (!per[lid]) continue;
      per[lid].count++;
      per[lid].classes[p.licenseClass] = (per[lid].classes[p.licenseClass] ?? 0) + 1;
    }
  }
  return per;
}

export function sumLandscapePersons(persons: Person[]): number {
  let s = 0;
  for (const p of persons) s += p.landscapeIds.length;
  return s;
}

/** Compliance-Risiko je Landschaft = Anteil über-berechtigter Personen. */
export function landscapeRisk(persons: Person[]) {
  const per: Record<string, { persons: number; lifted: number }> = {};
  for (const l of assignedLandscapes) per[l.id] = { persons: 0, lifted: 0 };
  for (const p of persons) {
    const over = p.leverIds.length > 0;
    for (const lid of p.landscapeIds) {
      if (!per[lid]) continue;
      per[lid].persons++;
      if (over) per[lid].lifted++;
    }
  }
  const out: Record<string, { persons: number; lifted: number; share: number; ampel: "gruen" | "gelb" | "rot" | "grau"; fueRelevant: boolean }> = {};
  for (const id of Object.keys(per)) {
    const share = per[id].persons ? per[id].lifted / per[id].persons : 0;
    // AP1: Ohne FUE-Metrik gibt es kein Über-Grant-Risiko im Lizenzsinn — nicht bewerten,
    // statt fälschlich „grün" zu melden.
    const fueRelevant = isFueRelevantLandscape(id);
    const ampel = !fueRelevant ? ("grau" as const) : share >= 0.3 ? ("rot" as const) : share >= 0.12 ? ("gelb" as const) : ("gruen" as const);
    out[id] = { ...per[id], share, ampel, fueRelevant };
  }
  return out;
}
